import { useCallback, useEffect, useRef, useState, type Dispatch, type SetStateAction } from 'react';

/**
 * useState that mirrors its value into localStorage under `key`.
 * Ported from the reference project; used for UI-only preferences
 * (collapsed sidebar, dismissed hints) that never belong on the server.
 */
export function usePersistedState<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
  const [state, setState] = useState<T>(() => {
    try {
      const stored = localStorage.getItem(key);
      if (stored !== null) return JSON.parse(stored) as T;
      localStorage.setItem(key, JSON.stringify(defaultValue));
    } catch {
      // Private-mode or quota failures fall back to the in-memory default.
    }
    return defaultValue;
  });

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch {
      // ignore storage errors
    }
  }, [key, state]);

  return [state, setState];
}

export function usePersistedCounter(key: string, defaultValue: number): [number, () => number] {
  const [counter, setCounter] = usePersistedState(key, defaultValue);
  const getNext = useCallback(() => {
    const next = counter;
    setCounter(counter + 1);
    return next;
  }, [counter, setCounter]);
  return [counter, getNext];
}
